import { hexDist, hexRing, hexToWorld } from '../hex.ts';
import { idiv, imin } from '../math/fixed.ts';
import type { BattleState, Bot } from '../types.ts';
import { ev, spawnEnemy } from './combat.ts';

/**
 * The runaway carts (Las vías 1, docs/SAGAS.md): Hilde's clan's train of mine carts, with no brake, down the fast way.
 * It is an escort whose caravan never stops: in along the first road, round the cavern on a ring of rail a few times,
 * and out along the second, quicker and quicker. Bone barricades rise on the rail ahead of it; one still standing when
 * the train gets there derails a cart (its share of the train's life). When the last lap starts, the drawbridge at the
 * second road's mouth goes up, and comes down only once its counterweight, at the far end of that road, is broken: a
 * train that gets to it while it is up goes over the edge.
 */

type Pt = { x: number; y: number };

/** The rail: the first road in, `laps` times round ring `ring`, round on to the second road and out along it. */
export const railTrack = (s: BattleState): { road: Pt[]; last: number; bridge: number } | null => {
  const r = s.rules?.rails;
  const [a, b] = s.map.sectors;
  if (!r || !a || !b) return null;
  const ring = hexRing(r.ring);
  const nearest = (g: { q: number; r: number }): number => {
    let best = 0;
    ring.forEach((h, i) => {
      if (hexDist(h, g) < hexDist(ring[best] as typeof h, g)) best = i;
    });
    return best;
  };
  const from = nearest(a.gate);
  const to = nearest(b.gate);
  const n = ring.length;
  const road: Pt[] = a.path.map((h) => hexToWorld(h.q, h.r));
  let last = 0;
  // Round the cavern, then on round to the second road's side.
  const steps = r.laps * n + ((to - from + n) % n);
  for (let k = 0; k <= steps; k++) {
    if (k === (r.laps - 1) * n) last = road.length;
    const h = ring[(from + k) % n] as { q: number; r: number };
    road.push(hexToWorld(h.q, h.r));
  }
  const bridge = road.length;
  for (const h of b.path.slice().reverse()) road.push(hexToWorld(h.q, h.r));
  return { road, last, bridge };
};

const train = (s: BattleState): Bot | undefined => (s.escort ? s.bots?.find((b) => b.id === s.escort?.cart && b.hp > 0) : undefined);

export const railsTick = (s: BattleState): void => {
  const r = s.rules?.rails;
  const e = s.escort;
  const st = s.rails;
  if (!r || !e || !st || !s.started || e.arrived) return;
  const cart = train(s);
  if (!cart) return;
  // Quicker and quicker, up to its top.
  e.speed = r.speed[0] + idiv((r.speed[1] - r.speed[0]) * imin(s.waveT, r.accel), r.accel);
  // A barricade rises on the rail ahead, now and then (none past the bridge).
  if (s.waveT >= st.next) {
    st.next = s.waveT + r.every;
    const at = e.next + r.ahead;
    const p = e.road[at];
    if (p && at < st.bridge && !st.walls.some((w) => w.at === at)) {
      const w = spawnEnemy(s, 'boneWall', 1, false, 0, p.x, p.y, r.hp);
      st.walls.push({ id: w.id, at });
      ev(s, { t: 'rails', kind: 'wall', x: p.x, y: p.y });
    }
  }
  // The train got to a barricade: still standing, it derails a cart (and goes to pieces); broken, it is gone.
  st.walls = st.walls.filter((w) => {
    const wall = s.enemies.find((x) => x.id === w.id && x.hp > 0);
    if (!wall) return false;
    if (e.next <= w.at) return true;
    wall.hp = 0;
    st.derailed++;
    cart.hp -= idiv(cart.maxHp, r.carts);
    ev(s, { t: 'rails', kind: 'derail', x: wall.x, y: wall.y });
    return false;
  });
  // The last lap: the bridge goes up, and its counterweight stands at the far end of the second road.
  if (!st.weight && e.next >= st.last) {
    const p = e.road[e.road.length - 1] as Pt;
    st.weight = spawnEnemy(s, 'boneWall', 1, false, 1, p.x, p.y, r.weight).id;
    const g = e.road[st.bridge] as Pt;
    ev(s, { t: 'rails', kind: 'up', x: g.x, y: g.y });
  }
  if (st.weight && !st.down && !s.enemies.some((x) => x.id === st.weight && x.hp > 0)) {
    st.down = true;
    const g = e.road[st.bridge] as Pt;
    ev(s, { t: 'rails', kind: 'down', x: g.x, y: g.y });
  }
  // Got to the bridge while it was up: over the edge.
  if (st.weight && !st.down && e.next > st.bridge) {
    cart.hp = 0;
    ev(s, { t: 'rails', kind: 'fall', x: cart.x, y: cart.y });
  }
};
